/* In-app notification center (Task-Management Phase 1 / T1).
 *
 * Backend surface (service/app/routers/workspace.py, session-scoped to the
 * signed-in user — decision D10): GET /me/notifications?unread=&limit= ->
 * {notifications, unreadCount}; POST /notifications/{id}/read (403 if not
 * yours); POST /notifications/read-all -> {ok, marked}.
 *
 * This is the DATA half — a tiny observable model that polls the feed, holds
 * the unread count, and applies optimistic read-state updates. The bell/feed
 * rendering lives in ws/notifBell.ts (subscribes to this), mirroring the
 * data/render split state/workspace.ts + ws/*.ts already use. Kept
 * dependency-light (only api/client.ts's request()) so the reducer logic is
 * unit-testable under Vitest's node env with a stubbed fetch, exactly like
 * tests/unit/api-client.test.ts.
 */
import { request } from '../api/client.ts';

export type NotifKind = 'assigned' | 'mention' | 'due' | 'stall' | 'regression' | string;

export interface Notification {
  id: number;
  kind: NotifKind;
  client_slug: string;
  task_id: number | null;
  actor: string | null;
  actor_user_id: number | null;
  body: string | null;
  dedup_key: string | null;
  created_at: string;
  read_at: string | null;
  /** The notification client's latest run file, resolved server-side (the
   * server owns slug→latest-run resolution) so the bell can deep-link
   * cross-client via the T2 taskLink helper without guessing it. Absent for a
   * client that has never been run (where a task can't exist anyway). */
  run_file?: string | null;
}

interface FeedResponse {
  notifications: Notification[];
  unreadCount: number;
}

class NotificationCenter {
  items: Notification[] = [];
  unreadCount = 0;
  private listeners = new Set<() => void>();
  private timer: ReturnType<typeof setInterval> | null = null;

  /** Subscribe to state changes (the bell re-renders on every change).
   * Returns an unsubscribe fn. */
  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(): void {
    this.listeners.forEach((fn) => fn());
  }

  /** Pull the latest feed + unread count. A failure leaves the prior state
   * untouched (a transient blip must not blank the bell); a 401 is handled by
   * request() itself (it re-shows the sign-in gate). */
  async refresh(): Promise<void> {
    try {
      const r = await request<FeedResponse>('/me/notifications?limit=50');
      this.items = r.notifications || [];
      this.unreadCount = r.unreadCount || 0;
      this.emit();
    } catch (e) {
      /* keep prior state — see doc comment */
    }
  }

  /** Mark one notification read. Optimistic: the UI updates immediately, the
   * POST reconciles in the background (a failure self-heals on the next
   * refresh, which re-reads the server's truth). */
  async markRead(id: number): Promise<void> {
    const n = this.items.find((x) => x.id === id);
    if (n && !n.read_at) {
      n.read_at = new Date().toISOString();
      this.unreadCount = Math.max(0, this.unreadCount - 1);
      this.emit();
    }
    try {
      await request(`/notifications/${id}/read`, { method: 'POST' });
    } catch (e) {
      /* self-heals on next refresh() */
    }
  }

  /** Mark every unread notification read (the feed panel's "mark all read"). */
  async markAllRead(): Promise<void> {
    const now = new Date().toISOString();
    let changed = false;
    this.items.forEach((n) => {
      if (!n.read_at) {
        n.read_at = now;
        changed = true;
      }
    });
    if (changed || this.unreadCount !== 0) {
      this.unreadCount = 0;
      this.emit();
    }
    try {
      await request('/notifications/read-all', { method: 'POST' });
    } catch (e) {
      /* self-heals on next refresh() */
    }
  }

  /** Start polling the feed. Fires an immediate refresh, then every
   * `intervalMs` (default 60s — no server push this phase). Idempotent: a
   * second start() is a no-op while a timer is already running. */
  start(intervalMs = 60000): void {
    if (this.timer !== null) return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), intervalMs);
  }

  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }
}

export const NOTIF = new NotificationCenter();
